// imports
import { useState } from 'react'
import { Button } from '@bosch/react-frok'
import { useTranslation } from 'react-i18next'

// components
import DraggableLineSelection from '@/ui/components/LineOnboarding/DraggableLineSelection'

// utils
import type { LineOption } from '@/utils/storage'

// styles
import './LineSelectionForm.scss'

type RowItem = { id: string; value: string }

interface LineSelectionFormProps {
    availableLines: LineOption[]
    initialLines?: LineOption[]
    onSave: (selectedLines: LineOption[]) => void
    onCancel: () => void
    isLoading?: boolean
}

const toRows = (lines: LineOption[]): RowItem[] =>
    lines.length === 0
        ? [{ id: 'row-0', value: '' }]
        : lines.map((line, index) => ({ id: `row-${index}`, value: line.value }))

// Save/cancel wrapper around the shared DraggableLineSelection (same rows as Settings and onboarding)
const LineSelectionForm = ({
    availableLines,
    initialLines = [],
    onSave,
    onCancel,
    isLoading = false,
}: LineSelectionFormProps) => {
    const { t } = useTranslation('general')
    const [rows, setRows] = useState<RowItem[]>(() => toRows(initialLines))

    const filledLines: LineOption[] = rows
        .map((row) => availableLines.find((line) => line.value === row.value))
        .filter(Boolean) as LineOption[]

    // an empty selection is valid: the user may remove all preferred lines
    const canSubmit = !isLoading

    return (
        <div className="line-selection-form">
            <div className="line-selection-form__content">
                <DraggableLineSelection
                    availableLines={availableLines}
                    rows={rows}
                    setRows={setRows}
                    labelPrefix={t('general:settings:lineLabel')}
                    placeholder={t('general:settings:linePlaceholder')}
                />
            </div>

            <div className="line-selection-form__actions">
                <Button
                    mode="primary"
                    label={t('general:buttons:save')}
                    disabled={!canSubmit}
                    onClick={() => onSave(filledLines)}
                />
                <Button
                    mode="secondary"
                    label={t('general:buttons:cancel')}
                    onClick={onCancel}
                    disabled={isLoading}
                />
            </div>
        </div>
    )
}

export default LineSelectionForm
